import type { CollegePartnerLogoItem, PartnerLogoItem } from './types'

export type OrganizationalPartner =
  | 'Zoom'
  | 'Coda'
  | 'KeyClub'
  | 'Matriculate'
  | 'Coalition'
  | 'TeachForAmerica'
  | 'SagaEducation'
  | 'KhanAcademy'
  | 'BigFuture'
  | 'CollegeBoard'
  | 'AlphaPhiOmega'

export const ORGANIZATIONAL_PARTNERS: Record<OrganizationalPartner, PartnerLogoItem> = {
  Zoom: {
    href: 'https://zoom.us/',
    logoSrc: 'zoom-logo-v3.svg',
    width: 356,
    height: 80,
    displayWidth: 250,
  },
  Coda: { href: 'https://coda.io/', logoSrc: 'coda.svg', width: 58, height: 90, displayWidth: 60 },
  KeyClub: {
    href: 'https://www.keyclub.org/',
    logoSrc: 'key-club-seal.png',
    displayWidth: 100,
    width: 453,
    height: 450,
  },
  Matriculate: {
    href: 'https://matriculate.org/',
    logoSrc: 'matriculate.png',
    displayWidth: 250,
    width: 500,
    height: 75,
  },
  Coalition: {
    href: 'https://www.coalitionforcollegeaccess.org/',
    logoSrc: 'coalition-logo-v2.png',
    width: 208,
    height: 56,
    displayWidth: 250,
  },
  TeachForAmerica: {
    href: 'https://www.teachforamerica.org/',
    logoSrc: 'tfa-logo-v2.png',
    displayWidth: 250,
    width: 4350,
    height: 2021,
  },
  SagaEducation: {
    href: 'https://saga.org/',
    logoSrc: 'saga-logo.png',
    displayWidth: 250,
    width: 1422,
    height: 727,
  },
  KhanAcademy: {
    href: 'https://www.khanacademy.org/',
    logoSrc: 'khan-academy.png',
    displayWidth: 250,
    width: 2308,
    height: 376,
  },
  BigFuture: {
    href: 'https://bigfuture.org/',
    logoSrc: 'BigFuture.png',
    displayWidth: 250,
    width: 500,
    height: 102,
  },
  CollegeBoard: {
    href: 'https://www.collegeboard.org/',
    logoSrc: 'college-board-logo.svg',
    displayWidth: 400,
    width: 361,
    height: 62,
  },
  AlphaPhiOmega: {
    href: 'https://apo.org/',
    logoSrc: 'apo_logo.png',
    displayWidth: 100,
    width: 518,
    height: 371,
  },
}

// Alphabetical order
export const PORTFOLIO_COLLEGE_PARTNERS = [
  'AmericanUniversity',
  'AmherstCollege',
  'Auburn',
  'Belmont',
  'Brown',
  'Caltech',
  'CarnegieMellon',
  'CaseWestern',
  'Colby',
  'CollegeOfWooster',
  'ColoradoSchoolOfMines',
  'Columbia',
  'Dartmouth',
  'DickinsonCollege',
  'Drexel',
  'Duke',
  'FloridaState',
  'FranklinMarshall',
  'GeorgeWashington',
  'GeorgiaTech',
  'JohnsHopkins',
  'MIT',
  'Northwestern',
  'NYU',
  'OlinCollege',
  'OSU',
  'PurdueGlobal',
  'PurdueUniversity',
  'ScrippsCollege',
  'SMU',
  'SNHU',
  'Spelman',
  'StOlafCollege',
  'Stanford',
  'Swarthmore',
  'UniversityOfArkansas',
  'UniversityOfBerkeley',
  'UniversityOfChicago',
  'UniversityOfDelaware',
  'UniversityOfDenver',
  'UniversityOfGeorgia',
  'UniversityOfIowa',
  'UniversityOfMaryland',
  'UniversityOfNotreDame',
  'UniversityOfPennsylvania',
  'USC',
  'UniversityOfTexasAustin',
  'UniversityOfVermont',
  'UniversityOfWisconsin',
  'Vanderbilt',
  'WashU',
  'WentworthInstitute',
  'WesleyanUniversity',
  'WillametteUniversity',
  'Yale',
] as const

export const DIALOGUES_COLLEGE_PARTNERS = [
  'Colby',
  'Columbia',
  'JohnsHopkins',
  'Northwestern',
  'NYU',
  'UniversityOfChicago',
  'WashU',
] as const

// Alphabetical order
export const STARS_COLLEGE_NETWORK_PARTNERS = [
  'AmherstCollege',
  'Auburn',
  'Brown',
  'Caltech',
  'CaseWestern',
  'Colby',
  'Columbia',
  'Dartmouth',
  'Duke',
  'GeorgiaTech',
  'MIT',
  'Northwestern',
  'OSU',
  'SMU',
  'Spelman',
  'Stanford',
  'UniversityOfAlabama',
  'UniversityOfArizona',
  'UniversityOfArkansas',
  'UniversityOfBerkeley',
  'UniversityOfChicago',
  'UniversityOfDenver',
  'UniversityOfIowa',
  'UniversityOfMaryland',
  'UniversityOfNotreDame',
  'UniversityOfSouthCarolina',
  'USC',
  'UniversityOfTexasAustin',
  'UniversityOfWisconsin',
  'Vanderbilt',
  'WashU',
  'Yale',
] as const

type CollegePartner =
  | (typeof PORTFOLIO_COLLEGE_PARTNERS)[number]
  | (typeof DIALOGUES_COLLEGE_PARTNERS)[number]
  | (typeof STARS_COLLEGE_NETWORK_PARTNERS)[number]

export const COLLEGE_PARTNER_DETAILS: Record<CollegePartner, CollegePartnerLogoItem> = {
  AmericanUniversity: {
    href: 'https://www.american.edu/',
    logoSrc: 'american-university-logo.png',
    displayWidth: 300,
    width: 940,
    height: 769,
    name: 'American University',
  },
  AmherstCollege: {
    href: 'https://www.amherst.edu/',
    logoSrc: 'Amherst-logo.svg',
    displayWidth: 300,
    width: 175,
    height: 25,
    name: 'Amherst College',
  },
  Auburn: {
    href: 'https://www.auburn.edu/',
    logoSrc: 'Auburn-logo.svg',
    displayWidth: 300,
    width: 217,
    height: 59,
    name: 'Auburn University',
  },
  Belmont: {
    href: 'https://www.belmont.edu/',
    logoSrc: 'Belmont-wordmark-logo.png',
    displayWidth: 300,
    width: 1461,
    height: 418,
    name: 'Belmont University',
  },
  Brown: {
    href: 'https://www.brown.edu/',
    logoSrc: 'brown-logo.svg',
    displayWidth: 270,
    width: 370,
    height: 178,
    name: 'Brown University',
  },
  Caltech: {
    href: 'https://www.caltech.edu/',
    logoSrc: 'caltech-logo.svg',
    displayWidth: 240,
    width: 208,
    height: 50,
    name: 'Caltech',
  },
  CarnegieMellon: {
    href: 'https://www.cmu.edu/',
    logoSrc: 'carnegie-mellon-university-logo.png',
    displayWidth: 300,
    width: 600,
    height: 383,
    name: 'Carnegie Mellon University',
  },
  CaseWestern: {
    href: 'https://case.edu/',
    logoSrc: 'Case-logo.png',
    displayWidth: 350,
    width: 384,
    height: 61,
    name: 'Case Western Reserve University',
  },
  Colby: {
    href: 'https://www.colby.edu/',
    logoSrc: 'colby-logo.svg',
    displayWidth: 200,
    width: 167,
    height: 89,
    name: 'Colby College',
  },
  CollegeOfWooster: {
    href: 'https://www.wooster.edu/',
    logoSrc: 'college-of-wooster-logo.png',
    displayWidth: 300,
    width: 2000,
    height: 526,
    name: 'The College of Wooster',
  },
  ColoradoSchoolOfMines: {
    href: 'https://www.mines.edu/',
    logoSrc: 'colorado-school-of-mines-logo.jpg',
    displayWidth: 300,
    width: 1438,
    height: 1365,
    name: 'Colorado School of Mines',
  },
  Columbia: {
    href: 'https://www.columbia.edu/',
    logoSrc: 'columbia-logo.svg',
    displayWidth: 350,
    width: 357,
    height: 55,
    name: 'Columbia University',
  },
  Dartmouth: {
    href: 'https://dartmouth.edu/',
    logoSrc: 'Dartmouth-logo.svg',
    displayWidth: 350,
    width: 512,
    height: 54,
    name: 'Dartmouth College',
  },
  DickinsonCollege: {
    href: 'https://www.dickinson.edu/',
    logoSrc: 'dickinson-college-logo.png',
    displayWidth: 300,
    width: 316,
    height: 63,
    name: 'Dickinson College',
  },
  Drexel: {
    href: 'https://drexel.edu/',
    logoSrc: 'drexel-university-logo.png',
    displayWidth: 300,
    width: 600,
    height: 153,
    name: 'Drexel University',
  },
  Duke: {
    href: 'https://duke.edu/',
    logoSrc: 'Duke-logo.svg',
    displayWidth: 300,
    width: 500,
    height: 500,
    name: 'Duke University',
  },
  FloridaState: {
    href: 'https://www.fsu.edu/',
    logoSrc: 'fsu-logo-v3.png',
    displayWidth: 300,
    width: 755,
    height: 258,
    name: 'Florida State University',
  },
  FranklinMarshall: {
    href: 'https://www.fandm.edu/',
    logoSrc: 'franklin-and-marshall-logo.jpg',
    displayWidth: 300,
    width: 1870,
    height: 480,
    name: 'Franklin & Marshall College',
  },
  GeorgeWashington: {
    href: 'https://www.gwu.edu/',
    logoSrc: 'george-washington-university-logo.png',
    displayWidth: 300,
    width: 600,
    height: 457,
    name: 'George Washington University',
  },
  GeorgiaTech: {
    href: 'https://www.gatech.edu/',
    logoSrc: 'georgiatech-logo-v2.png',
    displayWidth: 250,
    width: 1240,
    height: 328,
    name: 'Georgia Tech',
  },
  JohnsHopkins: {
    href: 'https://www.jhu.edu/',
    logoSrc: 'JohnsHopkins-logo-v2.png',
    displayWidth: 350,
    width: 744,
    height: 215,
    name: 'Johns Hopkins',
  },
  MIT: {
    href: 'https://www.mit.edu/',
    logoSrc: 'mit-logo.png',
    displayWidth: 300,
    width: 721,
    height: 161,
    name: 'MIT',
  },
  Northwestern: {
    href: 'https://www.northwestern.edu/',
    logoSrc: 'northwestern-logo.png',
    displayWidth: 350,
    width: 2471,
    height: 503,
    name: 'Northwestern',
  },
  NYU: {
    href: 'https://www.nyu.edu/',
    logoSrc: 'nyu-logo.png',
    displayWidth: 300,
    width: 1036,
    height: 383,
    name: 'New York University',
  },
  OlinCollege: {
    href: 'https://www.olin.edu/',
    logoSrc: 'olin-college-logo.png',
    displayWidth: 300,
    width: 1695,
    height: 324,
    name: 'Olin College of Engineering',
  },
  OSU: {
    href: 'https://www.osu.edu/',
    logoSrc: 'osu-logo.svg',
    displayWidth: 350,
    width: 263,
    height: 40,
    name: 'Ohio State University',
  },
  PurdueGlobal: {
    href: 'https://www.purdueglobal.edu/',
    logoSrc: 'purdue-university-global-logo.jpg',
    displayWidth: 300,
    width: 768,
    height: 384,
    name: 'Purdue University Global',
  },
  PurdueUniversity: {
    href: 'https://www.purdue.edu/',
    logoSrc: 'purdue-university-logo.jpg',
    displayWidth: 300,
    width: 1536,
    height: 768,
    name: 'Purdue University',
  },
  ScrippsCollege: {
    href: 'https://www.scrippscollege.edu/',
    logoSrc: 'scripps-college-logo-v2.png',
    displayWidth: 300,
    width: 2296,
    height: 792,
    name: 'Scripps College',
  },
  SMU: {
    href: 'https://www.smu.edu/',
    logoSrc: 'SMU-logo.png',
    displayWidth: 300,
    width: 332,
    height: 144,
    name: 'Southern Methodist University',
  },
  SNHU: {
    href: 'https://www.snhu.edu/',
    logoSrc: 'SNHU-logo.jpg',
    displayWidth: 300,
    width: 332,
    height: 144,
    name: 'Southern New Hampshire University',
  },
  Spelman: {
    href: 'https://www.spelman.edu/',
    logoSrc: 'spelman-logo.svg',
    displayWidth: 250,
    width: 2500,
    height: 2500,
    name: 'Spelman College',
  },
  StOlafCollege: {
    href: 'https://wp.stolaf.edu/',
    logoSrc: 'st-olaf-college-logo.png',
    displayWidth: 300,
    width: 200,
    height: 200,
    name: 'St. Olaf College',
  },
  Stanford: {
    href: 'https://www.stanford.edu/',
    logoSrc: 'Stanford-logo.svg',
    displayWidth: 300,
    width: 637,
    height: 246,
    name: 'Stanford University',
  },
  Swarthmore: {
    href: 'https://www.swarthmore.edu/',
    logoSrc: 'Swarthmore.png',
    displayWidth: 350,
    width: 1955,
    height: 194,
    name: 'Swarthmore College',
  },
  UniversityOfAlabama: {
    href: 'https://www.ua.edu/',
    logoSrc: 'Alabama-logo.svg',
    displayWidth: 300,
    width: 586,
    height: 151,
    name: 'University of Alabama',
  },
  UniversityOfArizona: {
    href: 'https://www.arizona.edu/',
    logoSrc: 'Arizona-logo.svg',
    displayWidth: 300,
    width: 137,
    height: 33,
    name: 'University of Arizona',
  },
  UniversityOfArkansas: {
    href: 'https://www.uark.edu/',
    logoSrc: 'Arkansas-Logo.png',
    displayWidth: 350,
    width: 768,
    height: 432,
    name: 'University of Arkansas',
  },
  UniversityOfBerkeley: {
    href: 'https://www.berkeley.edu/',
    logoSrc: 'berkeley-logo.svg',
    displayWidth: 300,
    width: 215,
    height: 68,
    name: 'University of California, Berkeley',
  },
  UniversityOfChicago: {
    href: 'https://www.uchicago.edu/',
    logoSrc: 'uchicago-logo.png',
    displayWidth: 300,
    width: 2000,
    height: 402,
    name: 'University of Chicago',
  },
  UniversityOfDelaware: {
    href: 'https://www.udel.edu/',
    logoSrc: 'university-of-delaware-logo.png',
    displayWidth: 300,
    width: 737,
    height: 737,
    name: 'University of Delaware',
  },
  UniversityOfDenver: {
    href: 'https://www.du.edu/',
    logoSrc: 'Denver-logo.svg',
    displayWidth: 300,
    width: 352,
    height: 81,
    name: 'University of Denver',
  },
  UniversityOfGeorgia: {
    href: 'https://www.uga.edu/',
    logoSrc: 'university-of-georgia-logo.png',
    displayWidth: 300,
    width: 2513,
    height: 1084,
    name: 'University of Georgia',
  },
  UniversityOfIowa: {
    href: 'https://uiowa.edu/',
    logoSrc: 'iowa-logo.png',
    displayWidth: 250,
    width: 1200,
    height: 600,
    name: 'University of Iowa',
  },
  UniversityOfMaryland: {
    href: 'https://www.umd.edu/',
    logoSrc: 'maryland-logo.png',
    displayWidth: 300,
    width: 1120,
    height: 248,
    name: 'University of Maryland',
  },
  UniversityOfNotreDame: {
    href: 'https://www.nd.edu/',
    logoSrc: 'NotreDame-logo.svg',
    displayWidth: 300,
    width: 211,
    height: 50,
    name: 'University of Notre Dame',
  },
  UniversityOfPennsylvania: {
    href: 'https://www.upenn.edu/',
    logoSrc: 'university-of-pennsylvania-logo.png',
    displayWidth: 300,
    width: 1600,
    height: 1067,
    name: 'University of Pennsylvania',
  },
  UniversityOfSouthCarolina: {
    href: 'https://www.sc.edu/',
    logoSrc: 'southcarolina-logo.svg',
    displayWidth: 300,
    width: 2500,
    height: 678,
    name: 'University of South Carolina',
  },
  USC: {
    href: 'https://www.usc.edu/',
    logoSrc: 'usc-logo.png',
    displayWidth: 210,
    width: 700,
    height: 394,
    name: 'USC',
  },
  UniversityOfTexasAustin: {
    href: 'https://www.utexas.edu/',
    logoSrc: 'Texas-logo.svg',
    displayWidth: 300,
    width: 350,
    height: 100,
    name: 'University of Texas at Austin',
  },
  UniversityOfVermont: {
    href: 'https://www.uvm.edu/',
    logoSrc: 'UVM-logo.png',
    displayWidth: 200,
    width: 3000,
    height: 2726,
    name: 'University of Vermont',
  },
  UniversityOfWisconsin: {
    href: 'https://www.wisc.edu/',
    logoSrc: 'wisconsin-logo.png',
    displayWidth: 300,
    width: 2700,
    height: 909,
    name: 'University of Wisconsin-Madison',
  },
  Vanderbilt: {
    href: 'https://www.vanderbilt.edu/',
    logoSrc: 'vanderbilt-logo.png',
    displayWidth: 300,
    width: 2560,
    height: 556,
    name: 'Vanderbilt University',
  },
  WashU: {
    href: 'https://wustl.edu/',
    logoSrc: 'washu-logo-v2.png',
    displayWidth: 250,
    width: 640,
    height: 236,
    name: 'Washington University in St. Louis',
  },
  WentworthInstitute: {
    href: 'https://wit.edu/',
    logoSrc: 'wentworth-institute-of-technology-logo.png',
    displayWidth: 500,
    width: 540,
    height: 340,
    name: 'Wentworth Institute of Technology',
  },
  WesleyanUniversity: {
    href: 'https://www.wesleyan.edu/',
    logoSrc: 'wesleyan-university-logo.jpg',
    displayWidth: 300,
    width: 600,
    height: 206,
    name: 'Wesleyan University',
  },
  WillametteUniversity: {
    href: 'https://willamette.edu/',
    logoSrc: 'willamette-university-logo.png',
    displayWidth: 300,
    width: 450,
    height: 112,
    name: 'Willamette University',
  },
  Yale: {
    href: 'https://www.yale.edu/',
    logoSrc: 'yale-logo.png',
    displayWidth: 180,
    width: 500,
    height: 216,
    name: 'Yale University',
  },
}

// Alphabetical order
export const STATE_PARTNERS = [
  'Arkansas',
  'Alaska',
  'Colorado',
  'Idaho',
  'Indiana',
  'Mississippi',
  'NewHampshire',
  'NewYork',
  'NorthDakota',
  'Ohio',
  'RhodeIsland',
  'Virginia',
]

type StatePartner = (typeof STATE_PARTNERS)[number]

export const STATE_PARTNER_DETAILS: Record<StatePartner, PartnerLogoItem> = {
  Arkansas: {
    href: 'https://ade.arkansas.gov/',
    logoSrc: 'ade-logo.png',
    displayWidth: 80,
    width: 576,
    height: 576,
  },
  Colorado: {
    href: 'https://coloradoedinitiative.org',
    logoSrc: 'cei-logo.png',
    greyscaleLogoSrc: 'cei-logo-greyscale.png',
    displayWidth: 120,
    width: 335,
    height: 152,
  },
  Mississippi: {
    href: 'https://www.mdek12.org/',
    logoSrc: 'mde-logo-transparent.png',
    greyscaleLogoSrc: 'mde-logo-greyscale.png',
    displayWidth: 130,
    width: 259,
    height: 100,
  },
  NewHampshire: {
    href: 'https://www.education.nh.gov/',
    logoSrc: 'NHDOE-logo.png',
    displayWidth: 240,
    width: 1004,
    height: 192,
  },
  Alaska: {
    href: 'https://education.alaska.gov/',
    logoSrc: 'alaska-logo.png',
    displayWidth: 80,
    width: 360,
    height: 317,
  },
  NewYork: {
    href: 'https://doe.nv.gov/',
    logoSrc: 'NDOE-name.png',
    displayWidth: 130,
    width: 341,
    height: 160,
  },
  RhodeIsland: {
    href: 'https://www.ride.ri.gov/',
    logoSrc: 'RIDE-logo.png',
    greyscaleLogoSrc: 'RIDE-logo-greyscale.png',
    displayWidth: 130,
    width: 744,
    height: 219,
  },
  NorthDakota: {
    href: 'https://www.nd.gov/dpi/',
    logoSrc: 'NDDPI-logo.png',
    displayWidth: 230,
    width: 2988,
    height: 1038,
  },
  Idaho: {
    href: 'https://www.sde.idaho.gov/',
    logoSrc: 'idaho-sde-logo-v2.png',
    displayWidth: 150,
    width: 674,
    height: 175,
  },
  Ohio: {
    href: 'https://education.ohio.gov/',
    logoSrc: 'transparent-ohio-logo.png',
    displayWidth: 150,
    width: 777,
    height: 116,
  },
  Indiana: {
    href: 'https://www.in.gov/doe/',
    logoSrc: 'indiana-logo.png',
    greyscaleLogoSrc: 'indiana-logo-greyscale.png',
    displayWidth: 100,
    width: 222,
    height: 80,
  },
  Virginia: {
    href: 'https://www.governor.virginia.gov/',
    logoSrc: 'virginia-seal2.png',
    displayWidth: 70,
    width: 512,
    height: 512,
  },
} as const

type Donor =
  | 'StandTogether'
  | 'IMC'
  | 'Mayfield'
  | 'Wilson'
  | 'OSullivan'
  | 'Citadel'
  | 'BarbieAndTony'
  | 'MayerPhillips'
  | 'SarahAndAlan'
  | 'BethAndRavenel'
  | 'Humphreys'
  | 'Ciocca'
  | 'Schmidt'
  | 'WFF'

export const DONORS: Record<Donor, PartnerLogoItem> = {
  StandTogether: {
    href: 'https://standtogether.org/',
    logoSrc: 'standtogether-logo.png',
    displayWidth: 150,
    width: 1518,
    height: 896,
  },
  IMC: {
    href: 'https://www.imc.com/us/foundation/',
    logoSrc: 'imc-foundation-logo.png',
    displayWidth: 200,
    width: 750,
    height: 138,
  },
  Mayfield: {
    href: 'https://www.mayfield.com/',
    logoSrc: 'Mayfield-logo.png',
    displayWidth: 200,
    width: 847,
    height: 360,
  },
  Wilson: {
    href: 'https://www.wilsonfoundation.care/',
    logoSrc: 'wilson-logo.jpg',
    displayWidth: 200,
    width: 472,
    height: 205,
  },
  OSullivan: {
    href: 'https://sosf.us/',
    logoSrc: 'osullivan-logo.svg',
    displayWidth: 150,
    width: 261,
    height: 93,
  },
  Citadel: {
    href: 'https://www.citadel.com/',
    logoSrc: 'citadel-logo.png',
    displayWidth: 200,
    width: 2072,
    height: 647,
  },
  BarbieAndTony: { logoSrc: 'BarbieAndTony.svg', displayWidth: 150, width: 138, height: 71 },
  MayerPhillips: { logoSrc: 'Mayer-Phillips.svg', displayWidth: 170, width: 157, height: 71 },
  SarahAndAlan: { logoSrc: 'Ketterer-Vorwald.svg', displayWidth: 200, width: 179, height: 56 },
  BethAndRavenel: {
    href: 'https://brcurryfoundation.org/',
    logoSrc: 'curry-foundation.png',
    displayWidth: 250,
    width: 3544,
    height: 531,
  },
  Humphreys: { logoSrc: 'humphreys.svg', displayWidth: 200, width: 179, height: 56 },
  Ciocca: { logoSrc: 'ciocca.svg', displayWidth: 200, width: 210, height: 42 },
  Schmidt: { logoSrc: 'schmidt-logo.png', displayWidth: 120, width: 300, height: 148 },
  WFF: { logoSrc: 'wff-logo.svg', displayWidth: 200, width: 218, height: 60 },
}
